import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { setSession } from '@/lib/session';

interface AuthResponse {
  user: { id: string; name: string };
  token: string;
}

// Inline signup + workspace naming used by the checkout flow. On success it
// persists the session, creates the named workspace, and calls onComplete(workspaceId)
// so the caller can continue straight to payment.
export default function SignupDialog({
  open,
  onOpenChange,
  onComplete,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (workspaceId: string) => void;
}) {
  const [form, setForm] = useState({ name: '', email: '', password: '', workspaceName: '' });
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const auth = await apiFetch<AuthResponse>('/auth/signup', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({ name: form.name, email: form.email, password: form.password }),
      });
      setSession({ token: auth.token, userName: auth.user.name });

      const ws = await apiFetch<{ workspace: { id: string } }>('/workspaces', {
        method: 'POST',
        body: JSON.stringify({ name: form.workspaceName.trim() || `${form.name}'s Workspace` }),
      });
      localStorage.setItem('workspaceId', ws.workspace.id);
      onComplete(ws.workspace.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Signup failed');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create your account</DialogTitle>
          <DialogDescription>Set up your account and workspace, then continue to payment.</DialogDescription>
        </DialogHeader>
        <form className="space-y-4 py-2" onSubmit={submit}>
          <div className="grid gap-2">
            <Label htmlFor="su-name">Full name</Label>
            <Input id="su-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="su-email">Email</Label>
            <Input id="su-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="su-password">Password</Label>
            <Input id="su-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="su-workspace">Workspace name</Label>
            <Input id="su-workspace" value={form.workspaceName} onChange={(e) => setForm({ ...form, workspaceName: e.target.value })} placeholder="My Company" required />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Please wait…' : 'Continue to payment'}
          </Button>
          <p className="text-sm text-muted-foreground text-center">
            Already have an account? <Link to="/login" className="text-primary hover:underline">Log in</Link>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
